import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { downloadUserAddresses, getUserAddresses } from '../api/Network';
import DownloadButton from '../components/DownloadButton';
import LoadingPage from '../components/LoadingPage';
import NewPagination from '../components/NewPagination';
import ResizableSplitContainer from '../components/ResizableSplitContainer';
import ProgressBarWidget from '../LowesPOC/ProgressBarWidget';
import RecordModal from '../LowesPOC/recordModal';
import { PaginationParameters, SortingParameters } from '../types/InputParameters';
import { UserAddress } from '../types/UserAddress';
import { CreateRecordModal } from './CreateRecordModal';
import { FilterModal } from './FilterModal';
import { UpdateRecordModal } from './UpdateRecordModal';
import { MyRoleContext } from '../contexts/AdminContext';

export type Filter = {
  key: string;
  value: string[];
};
export type PressHeaders = {
  name: string;
  description?: string;
  filterKey?: string;
};
const Records = () => {
  const { role } = useContext(MyRoleContext);
  const isReadOnly = role?.isReadOnly === true;
  const displayValue = (value: any) => {
    if (value === null || value === undefined || value === 'null') {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Y' : 'N';
    }

    return value;
  };

  const handleRecord = (record: UserAddress | null) => {
    setSelectedRecord(record);
    if (record && !isReadOnly) {
      (document.getElementById('edit_record_modal') as HTMLFormElement)?.showModal();
    } else {
      (document.getElementById('record_modal') as HTMLFormElement)?.showModal();
    }
  };
  const [selectedRecord, setSelectedRecord] = useState<UserAddress | null>(null);

  const [documentFilter, setDocumentFilter] = useState<string[]>([]);
  const [reasonFilter, setReasonFilter] = useState<string[]>([]);
  const [companyNameFilter, setCompanyNameFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string[]>([]);

  const [searchText, setSearchText] = useState<string | null>(null);
  const [debouncedSearchText, setDebouncedSearchText] = useState<string | null>(null);
  const [selectedFromDate, setSelectedFromDate] = useState<string | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<string | null>(null);
  const [tooltipsEnabled, setTooltipsEnabled] = useState<boolean>(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [totalItemCount, setTotalItemCount] = useState(0);
  const [paginationParameters, setPaginationParameters] = useState<PaginationParameters>({
    currentPage: 1,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingParameters>({
    sortByColumnName: '',
    asc: true,
  });

  const { data, isLoading, refetch, isFetching } = useQuery(
    [
      'userAddresses',
      paginationParameters.currentPage,
      paginationParameters.pageSize,
      debouncedSearchText,
      selectedFromDate,
      selectedToDate,
      documentFilter,
      reasonFilter,
      companyNameFilter,
      stateFilter,
      sorting,
    ],
    () =>
      getUserAddresses(
        paginationParameters,
        selectedFromDate,
        selectedToDate,
        documentFilter,
        reasonFilter,
        companyNameFilter,
        stateFilter,
        debouncedSearchText,
        sorting,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: isInitialized,
      onError: (error: Error) => toast.error(error.message),
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const today = new Date();
    const oneWeekAgo = new Date();

    oneWeekAgo.setDate(today.getDate() - 7);

    const formattedDate = oneWeekAgo?.toISOString().split('T')[0];
    setSelectedFromDate(formattedDate);
    setIsInitialized(true);
  }, []);

  const records = useMemo(() => {
    if (data?.data?.userAddresses == null) {
      return [];
    }
    const paginationHeaders = JSON.parse(data?.headers.get('x-pagination'));
    setTotalItemCount(paginationHeaders.TotalItemCount);

    const result: UserAddress[] = [...data.data.userAddresses];

    return result;
  }, [data?.data?.userAddresses]);

  const headers: PressHeaders[] = [
    { name: 'GUID', filterKey: 'Guid', description: 'GUID (unique user/address identifier).' },
    { name: 'FIRST NAME', filterKey: 'FirstName', description: 'First name.' },
    { name: 'LAST NAME', filterKey: 'LastName', description: 'Last name.' },
    { name: 'COMPANY NAME', filterKey: 'CompanyName', description: 'Company or organization name.' },
    {
      name: 'DOCUMENT TYPE',
      filterKey: 'DocumentType',
      description: 'Document type (e.g., letter, notice).',
    },
    { name: 'REASON', filterKey: 'Reason', description: 'Reason associated with the address or mailing.' },
    { name: 'ADDRESS', filterKey: 'AddressLine1', description: 'Primary street address.' },
    { name: 'CITY', filterKey: 'City', description: 'City.' },
    { name: 'STATE', filterKey: 'State', description: 'State or province code.' },
    { name: 'COUNTRY', filterKey: 'Country', description: 'Country.' },
    { name: 'ZIP', filterKey: 'Zip', description: 'ZIP or postal code.' },
    { name: 'CREATED BY', filterKey: 'CreatedBy', description: 'Creator username.' },
    { name: 'CREATED DATE', filterKey: 'CreatedDate', description: 'Creation date.' },
    { name: 'MAILED', filterKey: 'Mailed', description: 'Indicates if the mail has been sent.' },
    { name: 'MAILED DATE', filterKey: 'MailedDate', description: 'The date the mail was sent.' },
    {
      name: 'DELIVERY ADDRESS',
      filterKey: 'DeliveryAddress',
      description: 'Delivery street address (if different).',
    },
    { name: 'DELIVERY CITY', filterKey: 'DeliveryCity', description: 'Delivery city.' },
    { name: 'DELIVERY STATE', filterKey: 'DeliveryState', description: 'Delivery state or province code.' },
    { name: 'DELIVERY ZIP', filterKey: 'DeliveryZip', description: 'Delivery ZIP or postal code.' },
    { name: 'RETURN DATE', filterKey: 'ReturnDate', description: 'The date the mail was returned.' },
    { name: 'RETURN REASON', filterKey: 'ReturnReason', description: 'The reason the mail was returned.' },
    {
      name: 'INTELLIGENT MAIL BARCODE',
      filterKey: 'IntelligentMailBarcodeDigits',
      description: 'Intelligent Mail barcode digits.',
    },
    { name: 'INDEX', filterKey: 'Id', description: 'Internal numeric identifier (index).' },
  ];

  const clear = () => {
    setDocumentFilter([]);
    setReasonFilter([]);
    setCompanyNameFilter([]);
    setStateFilter([]);
    setSearchText(null);
    setDebouncedSearchText(null);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setSorting({
      sortByColumnName: '',
      asc: true,
    });
  };

  const handleSearchChange = (e: any) => {
    setSearchText(e.target.value);
  };
  const handleFromChange = (e: any) => {
    setSelectedFromDate(e.target.value); // Update state with the input value
  };
  const handleToChange = (e: any) => {
    setSelectedToDate(e.target.value); // Update state with the input value
  };

  const setSortingColumn = (columnNameToSortBy: string | undefined) => {
    let asc: boolean = sorting.asc;

    if (sorting.sortByColumnName === columnNameToSortBy) {
      asc = !asc;

      if (asc) {
        columnNameToSortBy = '';
      }
    } else {
      asc = true;
    }

    setSorting({
      sortByColumnName: columnNameToSortBy ?? '',
      asc: asc,
    });
  };

  const hasFilter = selectedFromDate || selectedToDate;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingPage />
      </div>
    );
  }
  const topPanel = (
    <div className="flex flex-col rounded-box p-2 bg-base-100 h-full min-h-0">
      <div className="flex flex-row  justify-between">
        <div className="flex flex-row flex-grow gap-4 items-center">
          <div className="text-primary text-3xl">Report Breakdown</div>
        </div>
        <div className="flex flex-row gap-4 flex-wrap ml-2 justify-end items-end items-center">
          {isFetching ? <div className="loading loading-dots text-primary " /> : null}
          <div className="flex items-center gap-2">
            <label htmlFor="searchText" className="text-sm">
              Search
            </label>
            <input
              id="searchText"
              type="text"
              placeholder="Type to search…"
              className={`input input-sm ${searchText ? 'border-primary' : 'border-base-300'}`}
              value={searchText ?? ''}
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="fromDate" className="text-sm">
              Start
            </label>
            <input
              id="fromDate"
              type="date"
              className={`input input-sm ${selectedFromDate ? 'border-primary' : 'border-base-300'}`}
              value={selectedFromDate ?? ''}
              onChange={handleFromChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="toDate" className="text-sm">
              End
            </label>
            <input
              id="toDate"
              type="date"
              className={`input input-sm ${selectedToDate ? 'border-primary' : 'border-base-300'}`}
              value={selectedToDate ?? ''}
              onChange={handleToChange}
            />
          </div>
          <DownloadButton
            onClick={async () => {
              await downloadUserAddresses(selectedFromDate, selectedToDate);
            }}
          />
          <div
            className={`btn btn-success btn-xs${isReadOnly ? ' btn-disabled' : ''}`}
            onClick={() => {
              if (!isReadOnly) {
                (document.getElementById('create_record_modal') as HTMLFormElement)?.showModal();
              }
            }}>
            Create Record
          </div>
        </div>
      </div>
      <div className="divider divider-primary m-0" />
      <div className="flex flex-row gap-2 justify-evenly flex-1 min-h-0 overflow-auto min-w-0">
        <ProgressBarWidget
          data={data?.data.documentTypeCounts}
          title="Document Type"
          property="documentType"
          filter={documentFilter}
          setFilter={setDocumentFilter}
        />
        <ProgressBarWidget
          data={data?.data.reasonCounts}
          title="Reason"
          property="reason"
          filter={reasonFilter}
          setFilter={setReasonFilter}
        />
        <ProgressBarWidget
          data={data?.data.companyNameCounts}
          title="Company Name"
          property="companyName"
          filter={companyNameFilter}
          setFilter={setCompanyNameFilter}
        />
        <ProgressBarWidget
          data={data?.data.stateCounts}
          title="State"
          property="state"
          filter={stateFilter}
          setFilter={setStateFilter}
        />
      </div>
    </div>
  );

  const bottomPanel = (
    <div className="flex flex-col h-full min-h-0 bg-base-100 rounded-box p-2 overflow-hidden">
      <div className="flex flex-row justify-between pb-2">
        <div className="text-primary text-3xl">Data</div>
        <div className="flex flex-row items-end gap-2">
          <button
            className="btn btn-outline btn-info btn-sm "
            onClick={() => (document.getElementById('filter_modal') as HTMLFormElement)?.showModal()}>
            Filter
          </button>
          <button className="btn btn-outline btn-primary btn-sm " onClick={clear}>
            Reset Filter
          </button>
        </div>
      </div>
      <div className="divider divider-primary m-0" />

      <div className="flex-1 min-h-0 overflow-auto">
        <table className={`table table-pin-rows border-separate border-spacing-0 `}>
          <thead>
            {/* headers */}
            <tr className="sticky top-0 z-20 bg-base-100 text-primary ">
              {headers.map((r) => {
                const { name, description, filterKey } = r;
                const headerParts = name.split(' ');
                const sortingText = sorting?.sortByColumnName === filterKey ? (sorting.asc ? ' 🞁' : ' 🞃') : '';
                // If the header text is too long, wrap it.
                const textElement =
                  headerParts.length === 2 && name.length > 10 ? (
                    <>
                      {headerParts[0]}
                      <br />
                      {headerParts[1] + sortingText}
                    </>
                  ) : (
                    name + sortingText
                  );
                const element = (
                  <div className="flex flex-row gap-2">
                    <button className="link link-hover" onClick={() => setSortingColumn(filterKey)}>
                      {textElement}
                    </button>
                  </div>
                );

                return (
                  <th key={r.name}>
                    <div
                      className={`${
                        r.description && tooltipsEnabled
                          ? 'tooltip tooltip-bottom before:text-start before:whitespace-pre-wrap before:content-[attr(data-tip)]'
                          : ''
                      }`}
                      data-tip={description}>
                      {element}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-base-100">
            {records?.map((record: UserAddress, index) => {
              return (
                <tr
                  key={record.id + record.guid}
                  className={`w-full hover:bg-primary hover:text-black ${
                    selectedRecord?.id === record.id ? 'bg-primary' : ''
                  }`}
                  onClick={() => handleRecord(selectedRecord?.id === record.id ? null : record)}>
                  <td>{displayValue(record.guid)}</td>
                  <td>{displayValue(record.firstName)}</td>
                  <td>{displayValue(record.lastName)}</td>
                  <td>{displayValue(record.companyName)}</td>
                  <td>{displayValue(record.documentType)}</td>
                  <td>{displayValue(record.reason)}</td>
                  <td>{displayValue(record.addressLine1)}</td>
                  <td>{displayValue(record.city)}</td>
                  <td>{displayValue(record.state)}</td>
                  <td>{displayValue(record.country)}</td>
                  <td>{displayValue(record.zip)}</td>
                  <td>{displayValue(record.createdBy)}</td>
                  <td>{displayValue(record.createdDate)}</td>
                  <td>{displayValue(record.mailed)}</td>
                  <td>{displayValue(record.mailedDate)}</td>
                  <td>{displayValue(record.deliveryAddress)}</td>
                  <td>{displayValue(record.deliveryCity)}</td>
                  <td>{displayValue(record.deliveryState)}</td>
                  <td>{displayValue(record.deliveryZip)}</td>
                  <td>{displayValue(record.returnDate)}</td>
                  <td>{displayValue(record.returnReason)}</td>
                  <td>{displayValue(record.intelligentMailBarcodeDigits)}</td>
                  <td>{displayValue(record.id)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-center">
        <NewPagination
          totalItems={totalItemCount}
          itemsPerPage={paginationParameters.pageSize}
          setItemsPerPage={(i) =>
            setPaginationParameters({
              ...paginationParameters,
              currentPage: 1,
              pageSize: i,
            })
          }
          currentPage={paginationParameters.currentPage}
          setCurrentPage={(i) =>
            setPaginationParameters({
              ...paginationParameters,
              currentPage: i,
            })
          }
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 h-full min-h-0 overflow-hidden bg-base-100 rounded p-2">
      <ResizableSplitContainer topElement={topPanel} bottomElement={bottomPanel} initialTopPercentage={40} />

      <RecordModal selectedRecord={selectedRecord} setSelectedRecord={setSelectedRecord} />
      <CreateRecordModal />
      <UpdateRecordModal selectedRecord={selectedRecord} onClose={() => setSelectedRecord(null)} />
      <FilterModal
        setReasonFilter={setReasonFilter}
        setCompanyNameFilter={setCompanyNameFilter}
        setStateFilter={setStateFilter}
        reasonFilter={reasonFilter}
        companyName={data?.data.companyNameCounts}
        states={data?.data.stateCounts}
      />
    </div>
  );
};
export default Records;
